// Mirrors Blog#generate_slug so the manager form can show what a post's URL will
// probably be while somebody is still typing the title.
//
// A preview, not the value: the server generates the real one, and only it can
// see whether the slug is already taken and needs a "-2" on the end. The form
// says "probably" for exactly that reason.
//
// Deliberately nothing but string arithmetic — there is no JS test runner in
// this project, so anything duplicated here has to be simple enough that it
// cannot quietly drift from the Ruby.
//
// The Ruby spec pins this table; if it ever changes, change it here too:
//   ("2026-09-10", "Round 3 Recap")       -> "2026-09-10-round-3-recap"
//   ("2026-09-10", "  Spacing   Test  ")  -> "2026-09-10-spacing-test"
//   ("2026-09-10", "Drift & Slide!")      -> "2026-09-10-drift-slide"
//   ("2026-09-10", "Café Motörhead")      -> "2026-09-10-cafe-motorhead"
//   ("2026-09-10", "")                    -> ""
//   ("",           "Round 3")             -> "<today>-round-3"

/** ActiveSupport's `String#parameterize`, for the cases a title actually hits:
 *  strip accents, drop anything that is not alphanumeric, collapse and trim the
 *  separators. */
export function parameterize(value: string): string {
  return (
    value
      .normalize("NFD")
      // Combining marks left behind by the decomposition above — "é" is now "e" + U+0301.
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9\-_]+/g, "-")
      .replace(/-{2,}/g, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase()
  );
}

/** The date half of the slug: the post's own publish date, or today for a draft
 *  that has not been given one. Takes the `datetime-local` string the form holds. */
function slugDate(publishAt: string): string {
  const parsed = publishAt ? new Date(publishAt) : new Date();
  const date = isNaN(parsed.getTime()) ? new Date() : parsed;

  // Local parts, not toISOString() — that converts to UTC and can land a post
  // written in the evening on the following day.
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${date.getFullYear()}-${month}-${day}`;
}

/** "<publish date>-<title>", or "" when there is no title to name it with. */
export function blogSlug(publishAt: string, title: string): string {
  const name = parameterize(title);
  if (!name) return "";

  return `${slugDate(publishAt)}-${name}`;
}
